import { useEffect, useRef, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/site/Logo";

const links = [
  { label: "Services", href: "#services", id: "services" },
  { label: "Abonnement", href: "#abonnement", id: "abonnement" },
  { label: "Réalisations", href: "#realisations", id: "realisations" },
  { label: "Méthode", href: "#methode", id: "methode" },
  { label: "À propos", href: "#a-propos", id: "a-propos" },
  { label: "FAQ", href: "#faq", id: "faq" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });
  const linksContainerRef = useRef(null);
  const linkRefs = useRef({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // Mount-once scroll listener; setScrolled is a stable React setter.
  }, []);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const sections = links
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry that is most visible near the top
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        // Trigger when section reaches just below the header
        rootMargin: "-20% 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
    // `links` is a module-level constant and never changes.
  }, []);

  // Move the indicator to the active link — repositions on resize / scroll
  useEffect(() => {
    const reposition = () => {
      const container = linksContainerRef.current;
      const el = linkRefs.current[activeId];
      if (!container || !el) {
        setIndicator((i) => ({ ...i, opacity: 0 }));
        return;
      }
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      setIndicator({
        left: eRect.left - cRect.left,
        width: eRect.width,
        opacity: 1,
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [activeId, scrolled]);

  const closeMobile = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F3F0FF]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <Logo size={44} variant="light" />
          <div className="flex flex-col leading-none">
            <span className="font-brand text-2xl md:text-[28px] font-medium tracking-tight text-aurora leading-none uppercase">
              Numedia
            </span>
            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.22em] text-[#0B1027]/60 font-bold mt-1.5">
              Par David Devos
            </span>
          </div>
        </a>

        <div
          ref={linksContainerRef}
          className="hidden md:flex items-center gap-9 relative h-full"
        >
          {links.map((l) => {
            const isActive = activeId === l.id;
            return (
              <a
                key={l.href}
                href={l.href}
                ref={(el) => (linkRefs.current[l.id] = el)}
                data-testid={`nav-link-${l.id}`}
                data-active={isActive ? "true" : "false"}
                className={`relative text-sm transition-colors duration-300 ${
                  isActive
                    ? "text-[#0B1027] font-semibold"
                    : "text-[#0F172A]/80 hover:text-[#0B1027]"
                }`}
              >
                {l.label}
              </a>
            );
          })}

          {/* Animated active indicator */}
          <span
            aria-hidden
            data-testid="nav-active-indicator"
            className="pointer-events-none absolute -bottom-2 h-[3px] rounded-full transition-all duration-500 ease-out"
            style={{
              left: indicator.left,
              width: indicator.width,
              opacity: indicator.opacity,
              background:
                "linear-gradient(90deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
              boxShadow: "0 0 12px rgba(168, 85, 247, 0.55)",
            }}
          />
        </div>

        <div className="hidden md:block">
          <Button
            asChild
            data-testid="nav-booking-cta"
            className="rounded-full btn-aurora h-11 px-6 text-xs uppercase tracking-[0.16em] font-semibold shadow-tactile hover-lift"
          >
            <a href="#reservation" className="inline-flex items-center gap-2">
              Réserver un appel
              <ArrowUpRight size={16} />
            </a>
          </Button>
        </div>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 -mr-2 text-[#0B1027]"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Fine gradient bottom border */}
      <div
        aria-hidden
        data-testid="nav-gradient-border"
        className={`pointer-events-none absolute bottom-0 left-0 right-0 h-px transition-opacity duration-500 ${
          scrolled ? "opacity-100" : "opacity-60"
        }`}
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(99,102,241,0.55) 18%, rgba(168,85,247,0.85) 50%, rgba(236,72,153,0.55) 82%, transparent 100%)",
        }}
      />

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-[#F3F0FF] border-t border-[#0B1027]/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => {
              const isActive = activeId === l.id;
              return (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={closeMobile}
                  className={`text-base flex items-center gap-3 ${
                    isActive ? "text-[#0B1027] font-semibold" : "text-[#0B1027]/80"
                  }`}
                >
                  <span
                    aria-hidden
                    className={`h-1.5 w-1.5 rounded-full transition-opacity ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                    style={{
                      background:
                        "linear-gradient(90deg, #6366F1, #A855F7, #EC4899)",
                    }}
                  />
                  {l.label}
                </a>
              );
            })}
            <Button
              asChild
              onClick={closeMobile}
              data-testid="nav-mobile-booking-cta"
              className="rounded-full btn-aurora h-12 mt-2"
            >
              <a href="#reservation">Réserver un appel</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
